import React, { useState } from 'react';
import type { EvidenceItem } from '../../types/evidence';
import evidenceAPI from '../../services/evidenceAPI';
import {
  X,
  Download,
  CheckCircle,
  XCircle,
  RefreshCw,
  FileText,
  AlertTriangle
} from 'lucide-react';

interface EvidenceDetailModalProps {
  evidenceId: number | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

const EvidenceDetailModal: React.FC<EvidenceDetailModalProps> = ({
  evidenceId,
  isOpen,
  onClose,
  onUpdate
}) => {
  const [evidence, setEvidence] = useState<EvidenceItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRecapturing, setIsRecapturing] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'data' | 'custody'>('details');

  React.useEffect(() => {
    if (isOpen && evidenceId) {
      loadEvidence();
    }
  }, [isOpen, evidenceId]);

  const loadEvidence = async () => {
    if (!evidenceId) return;

    setIsLoading(true);
    try {
      const data = await evidenceAPI.getEvidenceItemById(evidenceId);
      setEvidence(data);
    } catch (error) {
      console.error('Failed to load evidence:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!evidence) return;

    setIsDownloading(true);
    try {
      await evidenceAPI.downloadEvidence(evidence.id, evidence.evidence_name);
      alert(`✅ Evidence downloaded successfully!`);
    } catch (error) {
      console.error('Download failed:', error);
      alert('❌ Failed to download evidence. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRecapture = async () => {
    if (!evidence) return;

    setIsRecapturing(true);
    try {
      const updated = await evidenceAPI.recaptureEvidence(evidence.id);
      setEvidence(updated);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Recapture failed:', error);
      alert('Failed to recapture evidence');
    } finally {
      setIsRecapturing(false);
    }
  };

  const handleValidate = async (status: string) => {
    if (!evidence) return;

    try {
      const updated = await evidenceAPI.validateEvidence(evidence.id, status);
      setEvidence(updated);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Validation failed:', error);
      alert('Failed to validate evidence');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <FileText className="h-6 w-6 text-blue-600 flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-xl font-semibold text-gray-900 truncate">
                {evidence?.evidence_name || 'Loading...'}
              </h2>
              <p className="text-sm text-gray-500">{evidence?.evidence_id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0 ml-4"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : evidence ? (
          <>
            {/* Tabs */}
            <div className="border-b border-gray-200">
              <div className="flex gap-6 px-6">
                <button
                  onClick={() => setActiveTab('details')}
                  className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'details'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Details
                </button>
                <button
                  onClick={() => setActiveTab('data')}
                  className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'data'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Evidence Data
                </button>
                <button
                  onClick={() => setActiveTab('custody')}
                  className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'custody'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Chain of Custody
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'details' && (
                <div className="space-y-6">
                  {/* Description */}
                  {evidence.evidence_description && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-700 leading-relaxed">{evidence.evidence_description}</p>
                    </div>
                  )}

                  {/* Collection Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1 font-medium">Collection Method</p>
                      <p className="text-sm text-gray-900">
                        {evidence.collection_method === 'api_pull'
                          ? (evidence.source?.source_type === 'github' ? 'GitHub API'
                            : evidence.source?.source_type === 'gitlab' ? 'GitLab API'
                            : evidence.source?.source_type === 'bitbucket' ? 'Bitbucket API'
                            : evidence.source?.source_type === 'ssl_certificate' ? 'SSL Labs API'
                            : evidence.source?.source_type === 'aws_cloud' ? 'AWS API'
                            : evidence.source?.source_type === 'azure_cloud' ? 'Azure API'
                            : evidence.source?.source_type === 'gcp_cloud' ? 'GCP API'
                            : evidence.source?.source_type === 'siem_splunk' ? 'Splunk API'
                            : evidence.source?.source_type === 'siem_elastic' ? 'Elastic API'
                            : evidence.source?.source_type === 'siem_sentinel' ? 'Sentinel API'
                            : evidence.source?.source_type === 'jira' ? 'Jira API'
                            : evidence.source?.source_type === 'servicenow' ? 'ServiceNow API'
                            : (evidence.source?.source_name ? `${evidence.source.source_name} API` : 'API'))
                          : evidence.collection_method === 'manual_upload' ? 'Manual Upload'
                          : evidence.collection_method}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1 font-medium">Last Updated</p>
                      <p className="text-sm text-gray-900">
                        {(() => {
                          const date = new Date(evidence.collection_date);
                          const now = new Date();
                          const diff = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
                          if (diff < 1) return 'Just now';
                          if (diff < 24) return `${diff} hour${diff !== 1 ? 's' : ''} ago`;
                          const days = Math.floor(diff / 24);
                          return `${days} day${days !== 1 ? 's' : ''} ago`;
                        })()}
                      </p>
                    </div>
                  </div>

                  {/* Satisfies Compliance Requirements */}
                  {evidence.compliance_tags && evidence.compliance_tags.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-3">Satisfies Compliance Requirements</p>
                      <div className="space-y-2">
                        {evidence.compliance_tags.map((tag, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm">
                            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-900 font-medium">{tag}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Auditor Validation Checklist */}
                  {evidence.evidence_data && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-sm font-semibold text-green-900 mb-3 flex items-center gap-2">
                        <CheckCircle className="h-5 w-5" />
                        Auditor Validation Checklist
                      </p>
                      <div className="space-y-3">
                        {/* Dynamic checklist based on evidence type */}
                        {evidence.evidence_type === 'github_pr_approval' && evidence.evidence_data.approvals && (
                          <>
                            <div className="flex items-start gap-2 text-sm">
                              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-green-900 font-medium">Pull requests require 2+ approvals</p>
                                <p className="text-green-700 text-xs">Evidence: {evidence.evidence_data.approvals.length} approval(s) found</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2 text-sm">
                              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-green-900 font-medium">Code review process documented</p>
                                <p className="text-green-700 text-xs">Evidence: PR #{evidence.evidence_data.pr_number} with review history</p>
                              </div>
                            </div>
                          </>
                        )}
                        {evidence.evidence_type === 'ssl_certificate' && evidence.evidence_data.expiration_date && (
                          <>
                            <div className="flex items-start gap-2 text-sm">
                              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-green-900 font-medium">Certificate is valid and not expired</p>
                                <p className="text-green-700 text-xs">Expires: {new Date(evidence.evidence_data.expiration_date).toLocaleDateString()}</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2 text-sm">
                              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-green-900 font-medium">Strong cipher suites only (TLS 1.3)</p>
                                <p className="text-green-700 text-xs">TLS version: {evidence.evidence_data.tls_version || 'TLS 1.3'}</p>
                              </div>
                            </div>
                          </>
                        )}
                        {evidence.evidence_type === 'github_commit_history' && evidence.evidence_data.total_commits && (
                          <>
                            <div className="flex items-start gap-2 text-sm">
                              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-green-900 font-medium">Version control system in use</p>
                                <p className="text-green-700 text-xs">Evidence: {evidence.evidence_data.total_commits} commits tracked</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2 text-sm">
                              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-green-900 font-medium">Audit trail maintained</p>
                                <p className="text-green-700 text-xs">Evidence: Full commit history available</p>
                              </div>
                            </div>
                          </>
                        )}
                        {evidence.evidence_type === 'github_pr_summary' && evidence.evidence_data.total_prs && (
                          <>
                            <div className="flex items-start gap-2 text-sm">
                              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-green-900 font-medium">All PRs require 2+ approvals before merge</p>
                                <p className="text-green-700 text-xs">Evidence: {evidence.evidence_data.approval_percentage}% compliance ({evidence.evidence_data.with_2_plus_approvals}/{evidence.evidence_data.total_prs} PRs)</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2 text-sm">
                              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-green-900 font-medium">Code review turnaround time tracked</p>
                                <p className="text-green-700 text-xs">Evidence: Average {evidence.evidence_data.average_review_time_hours} hours review time</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2 text-sm">
                              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-green-900 font-medium">Protected branch policy enforced</p>
                                <p className="text-green-700 text-xs">Evidence: {evidence.evidence_data.direct_commits_to_main} direct commits to protected branches</p>
                              </div>
                            </div>
                          </>
                        )}
                        {/* Generic validation for other types */}
                        {!['github_pr_approval', 'ssl_certificate', 'github_commit_history', 'github_pr_summary'].includes(evidence.evidence_type) && (
                          <>
                            <div className="flex items-start gap-2 text-sm">
                              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-green-900 font-medium">Evidence collected and validated</p>
                                <p className="text-green-700 text-xs">Status: {evidence.validation_status}</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2 text-sm">
                              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-green-900 font-medium">Compliance requirements mapped</p>
                                <p className="text-green-700 text-xs">Evidence: {evidence.compliance_tags?.length || 0} tags assigned</p>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Staleness Warning */}
                  {evidence.is_stale && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-orange-900 mb-1">
                            Evidence is Stale - Update Needed
                          </h4>
                          <p className="text-sm text-orange-700">
                            This evidence is {evidence.staleness_status}. Recapture it to ensure compliance.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Metadata Grid */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Evidence Date</p>
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(evidence.evidence_date).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Source</p>
                        <p className="text-sm font-medium text-gray-900">
                          {evidence.source?.source_name || 'Manual Upload'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Storage Type</p>
                        <p className="text-sm font-medium text-gray-900 capitalize">{evidence.storage_type?.replace('_', ' ')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Status</p>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${evidenceAPI.getStatusColor(evidence.validation_status)}`}>
                          {evidence.validation_status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'data' && (
                <div>
                  {evidence.evidence_data ? (
                    <>
                      {evidence.evidence_type === 'screenshot_capture' && evidence.evidence_data.captured_at && (
                        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h3 className="text-sm font-semibold text-blue-900 mb-2">Screenshot Details</h3>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-blue-700 font-medium">Captured URL:</p>
                              <p className="text-blue-900 break-all">{evidence.evidence_data.capture_url || evidence.evidence_data.user?.email}</p>
                            </div>
                            <div>
                              <p className="text-blue-700 font-medium">Captured At:</p>
                              <p className="text-blue-900">{new Date(evidence.evidence_data.captured_at || evidence.evidence_data.timestamp).toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-blue-700 font-medium">File Size:</p>
                              <p className="text-blue-900">{(evidence.evidence_data.file_size || evidence.file_size || 0 / 1024).toFixed(2)} KB</p>
                            </div>
                            <div>
                              <p className="text-blue-700 font-medium">Hash (SHA-256):</p>
                              <p className="text-blue-900 font-mono text-xs truncate" title={evidence.evidence_data.file_hash || evidence.file_hash}>
                                {(evidence.evidence_data.file_hash || evidence.file_hash || '').substring(0, 16)}...
                              </p>
                            </div>
                            {evidence.evidence_data.user_email && (
                              <div className="col-span-2">
                                <p className="text-blue-700 font-medium">Captured By:</p>
                                <p className="text-blue-900">{evidence.evidence_data.user_name || evidence.evidence_data.user_email}</p>
                              </div>
                            )}
                            {evidence.evidence_data.purpose && (
                              <div className="col-span-2">
                                <p className="text-blue-700 font-medium">Purpose:</p>
                                <p className="text-blue-900">{evidence.evidence_data.purpose}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      <pre className="bg-gray-50 rounded-lg p-4 text-xs text-gray-900 overflow-x-auto">
                        {JSON.stringify(evidence.evidence_data, null, 2)}
                      </pre>
                    </>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-8 text-center">
                      <p className="text-gray-600">No evidence data available</p>
                      {evidence.metadata && (
                        <div className="mt-4 text-left">
                          <p className="text-sm font-medium text-gray-700 mb-2">Metadata:</p>
                          <pre className="bg-white rounded-lg p-4 text-xs text-gray-900 overflow-x-auto">
                            {JSON.stringify(evidence.metadata, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'custody' && (
                <div className="space-y-3">
                  {evidence.custody_chain && evidence.custody_chain.length > 0 ? (
                    evidence.custody_chain.map((entry, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{entry.action}</p>
                            <p className="text-xs text-gray-600 mt-1">
                              User ID: {entry.user_id}
                            </p>
                          </div>
                          <p className="text-xs text-gray-500">
                            {new Date(entry.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-600">No custody chain entries</p>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="border-t border-gray-200 p-6 bg-gray-50">
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleDownload}
                  disabled={isDownloading}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Download className={`h-4 w-4 ${isDownloading ? 'animate-pulse' : ''}`} />
                  {isDownloading ? 'Preparing Package...' : '📥 Download Evidence Package'}
                </button>

                {evidence.is_stale && (
                  <button
                    onClick={handleRecapture}
                    disabled={isRecapturing}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <RefreshCw className={`h-4 w-4 ${isRecapturing ? 'animate-spin' : ''}`} />
                    {isRecapturing ? 'Recapturing...' : 'Recapture Now'}
                  </button>
                )}

                {evidence.validation_status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleValidate('approved')}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Approve
                    </button>
                    <button
                      onClick={() => handleValidate('rejected')}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <XCircle className="h-4 w-4" />
                      Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default EvidenceDetailModal;
